import { useEffect, useRef, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Info, X, ShoppingCart, Navigation } from 'lucide-react';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const getPirateShipIcon = (accentColor = '#c084fc') => L.divIcon({
  className: 'user-location-marker',
  html: `
    <div style="position:relative;filter:drop-shadow(2px 2px 8px rgba(0,0,0,0.7));">
      <img src="/pirate-ship.png" alt="you" style="width:44px;height:44px;object-fit:contain;display:block;background:transparent;" />
      <div style="
        position:absolute;top:50%;left:50%;
        transform:translate(-50%,-50%);
        width:56px;height:56px;
        border:2px solid ${accentColor}cc;
        border-radius:50%;
        animation:rideShipPing 2.5s cubic-bezier(0,0,0.2,1) infinite;
        pointer-events:none;
      "></div>
      <style>
        @keyframes rideShipPing {
          0%   { transform:translate(-50%,-50%) scale(0.7); opacity:1; }
          75%,100% { transform:translate(-50%,-50%) scale(1.8); opacity:0; }
        }
      </style>
    </div>`,
  iconSize: [44, 44],
  iconAnchor: [22, 38],
});

const makeCircleIcon = (color) =>
  L.divIcon({
    html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 3px 10px rgba(0,0,0,0.55)"></div>`,
    className: '', iconAnchor: [9, 9], iconSize: [18, 18],
  });

const makeSquareIcon = (color) =>
  L.divIcon({
    html: `<div style="width:18px;height:18px;border-radius:3px;background:${color};border:3px solid #fff;box-shadow:0 3px 10px rgba(0,0,0,0.55)"></div>`,
    className: '', iconAnchor: [9, 9], iconSize: [18, 18],
  });

function FitBounds({ points }) {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (fitted.current || !points || points.length < 2) return;
    map.fitBounds(L.latLngBounds(points), { padding: [48, 80], maxZoom: 15 });
    fitted.current = true;
  }, [map, points]);
  return null;
}

function MapRefCapture({ mapRef }) {
  const map = useMap();
  useEffect(() => { mapRef.current = map; }, [map, mapRef]);
  return null;
}

async function fetchOsrmRoute(from, to) {
  try {
    const url = `https://router.project-osrm.org/route/v1/driving/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    const coords = data?.routes?.[0]?.geometry?.coordinates;
    if (!coords) return null;
    return coords.map(([lng, lat]) => [lat, lng]);
  } catch { return null; }
}

function haversine(a, b) {
  if (!a?.lat || !b?.lat) return null;
  const R = 3958.8, dLat = (b.lat - a.lat) * Math.PI / 180, dLng = (b.lng - a.lng) * Math.PI / 180;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return (2 * R * Math.asin(Math.sqrt(s))).toFixed(1);
}

const VEHICLE_LABELS = { sedan: 'Sedan', suv: 'SUV', van: 'Van / Minivan', limo: 'Limousine', luxury: 'Luxury', truck: 'Pickup Truck', any: 'Any Vehicle' };

function fmtDT(v) {
  if (!v) return null;
  try { return new Date(v).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return null; }
}

export default function RideRouteMap({ item, onClaim, theme }) {
  const mapRef = useRef(null);
  const pickupCoords = item?.pickupCoordinates;
  const destCoords   = item?.destinationCoordinates;

  const [routePoints,   setRoutePoints]   = useState(null);
  const [driverPos,     setDriverPos]     = useState(null);
  const [infoPanelOpen, setInfoPanelOpen] = useState(false);
  const watchId = useRef(null);

  const hasPickup = pickupCoords?.lat && pickupCoords?.lng;
  const hasDest   = destCoords?.lat   && destCoords?.lng;
  const accent    = theme?.colors?.accent || '#3b82f6';

  // Fetch OSRM road route
  useEffect(() => {
    if (!hasPickup || !hasDest) return;
    fetchOsrmRoute(pickupCoords, destCoords).then(pts => {
      setRoutePoints(pts || [
        [pickupCoords.lat, pickupCoords.lng],
        [destCoords.lat,   destCoords.lng],
      ]);
    });
  }, [pickupCoords?.lat, pickupCoords?.lng, destCoords?.lat, destCoords?.lng]);

  // Track driver GPS
  const startTracking = useCallback(() => {
    if (!navigator.geolocation) return;
    watchId.current = navigator.geolocation.watchPosition(
      pos => setDriverPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      null,
      { enableHighAccuracy: true, maximumAge: 5000 }
    );
  }, []);

  useEffect(() => {
    startTracking();
    return () => { if (watchId.current != null) navigator.geolocation.clearWatch(watchId.current); };
  }, [startTracking]);

  const center = hasPickup
    ? hasDest
      ? [(pickupCoords.lat + destCoords.lat) / 2, (pickupCoords.lng + destCoords.lng) / 2]
      : [pickupCoords.lat, pickupCoords.lng]
    : [37.7749, -122.4194];

  const fitPoints = [
    hasPickup ? [pickupCoords.lat, pickupCoords.lng] : null,
    hasDest   ? [destCoords.lat,   destCoords.lng]   : null,
  ].filter(Boolean);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const mapsUrl = hasPickup && hasDest ? (() => {
    const pickup = `${pickupCoords.lat},${pickupCoords.lng}`;
    const dest   = `${destCoords.lat},${destCoords.lng}`;
    if (driverPos) {
      const origin = `${driverPos.lat},${driverPos.lng}`;
      return isIOS
        ? `maps://maps.apple.com/?saddr=${origin}&daddr=${pickup}&daddr=${dest}&dirflg=d`
        : `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}&waypoints=${pickup}&travelmode=driving`;
    }
    return isIOS
      ? `maps://maps.apple.com/?saddr=${pickup}&daddr=${dest}&dirflg=d`
      : `https://www.google.com/maps/dir/?api=1&origin=${pickup}&destination=${dest}&travelmode=driving`;
  })() : null;

  const dist  = haversine(pickupCoords, destCoords);
  const addr1 = s => s ? s.split(',')[0] : null;

  const infoRows = [
    hasPickup && { icon: '📍', label: 'Pickup',      value: addr1(item?.pickupAddress)      || 'Pickup' },
    hasDest   && { icon: '🏁', label: 'Drop-off',    value: addr1(item?.destinationAddress) || 'Destination' },
    dist      && { icon: '🛣️', label: 'Distance',    value: `~${dist} mi` },
    fmtDT(item?.rideDateTime) && { icon: '📅', label: 'Date & Time', value: fmtDT(item.rideDateTime) },
    item?.vehicleType && item.vehicleType !== 'any' && { icon: '🚗', label: 'Vehicle', value: VEHICLE_LABELS[item.vehicleType] || item.vehicleType },
    item?.passengers > 1 && { icon: '👥', label: 'Passengers', value: `${item.passengers} passengers` },
    item?.price && { icon: '💰', label: 'Pay',   value: `$${item.price}` },
    item?.description && { icon: '📝', label: 'Notes', value: item.description },
  ].filter(Boolean);

  const iconBtn = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', outline: 'none',
  };

  // Bottom bar height so map padding accounts for it
  const BAR_H = 58;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', background: theme?.colors?.background || '#0f0a14', overflow: 'hidden' }}>
      <style>{`
        .ride-route-map-wrap .leaflet-tile-pane {
          filter: brightness(0.93) hue-rotate(12deg) saturate(1.35) contrast(1.06);
        }
      `}</style>

      {/* ── Map ── */}
      <MapContainer
        center={center}
        zoom={13}
        style={{ width: '100%', height: '100%' }}
        zoomControl={false}
        attributionControl={false}
        className="ride-route-map-wrap"
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          maxZoom={19}
        />
        {fitPoints.length >= 2 && <FitBounds points={fitPoints} />}
        {routePoints && <Polyline positions={routePoints} color="rgba(255,255,255,0.85)" weight={9} opacity={1} />}
        {routePoints && <Polyline positions={routePoints} color={accent || '#2563eb'} weight={5} opacity={1} />}
        {hasPickup && <Marker position={[pickupCoords.lat, pickupCoords.lng]} icon={makeCircleIcon('#4ade80')} />}
        {hasDest   && <Marker position={[destCoords.lat,   destCoords.lng]}   icon={makeSquareIcon('#f87171')} />}
        {driverPos && <Marker position={[driverPos.lat, driverPos.lng]}       icon={getPirateShipIcon(accent)} />}
        <MapRefCapture mapRef={mapRef} />
      </MapContainer>

      {/* ── Legend top-left ── */}
      <div onClick={e => e.stopPropagation()} style={{
        position: 'absolute', top: 10, left: 10, zIndex: 1000,
        background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(8px)',
        borderRadius: 10, padding: '6px 10px',
        display: 'flex', flexDirection: 'column', gap: 4,
        fontSize: '0.7rem', color: '#fff', pointerEvents: 'none',
        border: '1px solid rgba(255,255,255,0.12)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#4ade80', flexShrink: 0 }} />
          <span style={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.9 }}>
            {addr1(item?.pickupAddress) || 'Pickup'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: '#f87171', flexShrink: 0 }} />
          <span style={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', opacity: 0.9 }}>
            {addr1(item?.destinationAddress) || 'Destination'}
          </span>
        </div>
        {driverPos && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <img src="/pirate-ship.png" alt="you" style={{ width: 14, height: 14, objectFit: 'contain', flexShrink: 0 }} />
            <span style={{ opacity: 0.9 }}>You</span>
          </div>
        )}
      </div>

      {/* ── Info button top-right ── */}
      <button
        onClick={e => { e.stopPropagation(); setInfoPanelOpen(o => !o); }}
        style={{
          ...iconBtn,
          position: 'absolute', top: 10, right: 10, zIndex: 1001,
          width: 36, height: 36, borderRadius: '50%',
          background: infoPanelOpen ? accent : 'rgba(0,0,0,0.72)',
          backdropFilter: 'blur(8px)',
          border: `1px solid ${infoPanelOpen ? accent : 'rgba(255,255,255,0.2)'}`,
          color: '#fff', transition: 'background 0.2s, border-color 0.2s',
        }}
      >
        {infoPanelOpen ? <X size={16} /> : <Info size={16} />}
      </button>

      {/* ── Zoom controls ── */}
      <div onClick={e => e.stopPropagation()} style={{
        position: 'absolute', bottom: BAR_H + 10, left: 10, zIndex: 1000,
        display: 'flex', flexDirection: 'column', gap: 4,
      }}>
        {[{ l: '+', f: () => mapRef.current?.zoomIn() }, { l: '−', f: () => mapRef.current?.zoomOut() }].map(({ l, f }) => (
          <button key={l} onClick={e => { e.stopPropagation(); f(); }} style={{
            ...iconBtn, width: 32, height: 32, borderRadius: 8,
            background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,255,255,0.18)',
            color: '#fff', fontSize: '1.1rem', fontWeight: 700,
          }}>{l}</button>
        ))}
      </div>

      {/* ── Ride-detail info panel (slides up above the bottom bar) ── */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'absolute', bottom: BAR_H, left: 0, right: 0, zIndex: 1003,
          background: 'rgba(8,8,14,0.97)', backdropFilter: 'blur(16px)',
          borderTop: `1px solid ${accent}40`,
          borderRadius: '14px 14px 0 0',
          maxHeight: infoPanelOpen ? '55%' : '0',
          overflow: 'hidden',
          transition: 'max-height 0.32s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        <div style={{ padding: '0.9rem 1rem 0.75rem', overflowY: 'auto', maxHeight: '55vh' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.7rem', paddingBottom: '0.55rem', borderBottom: `1px solid rgba(255,255,255,0.09)` }}>
            <span style={{ fontSize: '1.05rem' }}>🚗</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>{item?.name || 'Ride'}</div>
              <div style={{ fontSize: '0.65rem', color: accent, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Ride Request</div>
            </div>
            {item?.price && <div style={{ fontSize: '1rem', fontWeight: 800, color: '#4ade80' }}>${item.price} pay</div>}
          </div>

          {/* Detail rows */}
          {infoRows.map(({ icon, label, value }) => (
            <div key={label} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', padding: '0.35rem 0', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <span style={{ fontSize: '0.88rem', flexShrink: 0, width: '1.25rem', textAlign: 'center', marginTop: '0.1rem' }}>{icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: accent, opacity: 0.75, marginBottom: '0.05rem' }}>{label}</div>
                <div style={{ fontSize: '0.83rem', color: 'rgba(255,255,255,0.88)', lineHeight: 1.4 }}>{value}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Persistent bottom action bar ── */}
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 1004,
          height: BAR_H,
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0 0.75rem',
          background: 'rgba(8,8,14,0.96)', backdropFilter: 'blur(14px)',
          borderTop: `1px solid rgba(255,255,255,0.09)`,
        }}
      >

        {/* Navigate */}
        {mapsUrl ? (
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            style={{
              ...iconBtn,
              flex: 1, height: 40, borderRadius: 10, gap: '0.35rem',
              background: `${accent}20`,
              border: `1px solid ${accent}60`,
              color: accent, textDecoration: 'none',
              fontSize: '0.82rem', fontWeight: 700,
            }}
          >
            <Navigation size={15} />
            Navigate
          </a>
        ) : (
          <div style={{ flex: 1 }} />
        )}

        {/* Claim Ride */}
        <button
          onClick={e => { e.stopPropagation(); onClaim && onClaim(item); }}
          style={{
            ...iconBtn,
            flex: 2, height: 40, borderRadius: 10, gap: '0.38rem',
            background: accent,
            border: 'none',
            color: '#fff', fontSize: '0.86rem', fontWeight: 700,
            boxShadow: `0 3px 12px ${accent}55`,
          }}
        >
          <ShoppingCart size={15} />
          Request - ${item?.price} pay
        </button>
      </div>
    </div>
  );
}
